import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert, AppBar, Box, Chip, CircularProgress, Container, Grid, IconButton, Paper, Stack, Toolbar, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import api, { KRONOS } from '../../infrastructure/config/api';
import SymbolSelectWithWatchButton from '../components/symbols/SymbolSelectWithWatchButton';
import SymbolCard from '../components/symbols/SymbolCard';
import OrderConfirmDialog from '../components/symbols/OrderConfirmDialog';
import { watchlistService } from '../../application/services/watchlist-service';
import { tradeOrderService } from '../../application/services/trade-order-service';

const KRONOS_MIN_EDGE_PCT = 0.2;

export default function SymbolGridPage() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [kronosBusySymbol, setKronosBusySymbol] = useState('');
  const [kronosAvailable, setKronosAvailable] = useState(false);
  const [kronosResult, setKronosResult] = useState(null);
  const [order, setOrder] = useState(null);
  const watchedSymbols = useMemo(() => new Set(items.map((item) => item.symbol)), [items]);
  const counts = useMemo(() => ({
    LONG: items.filter((item) => item.watch_side === 'LONG').length,
    SHORT: items.filter((item) => item.watch_side === 'SHORT').length,
  }), [items]);
  const groupedItems = useMemo(() => ({
    LONG: items.filter((item) => item.watch_side === 'LONG'),
    SHORT: items.filter((item) => item.watch_side === 'SHORT'),
  }), [items]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await watchlistService.list()); }
    catch (error) { enqueueSnackbar(error.response?.data?.error || 'Không tải được watchlist', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { if (user.username === 'khanh') load(); }, [load, user.username]);
  useEffect(() => {
    if (user.username !== 'khanh') return;
    let mounted = true;
    api.get(KRONOS.STATUS)
      .then((response) => {
        if (!mounted) return;
        const data = response.data?.data;
        setKronosAvailable(Boolean(data?.available));
      })
      .catch(() => {
        if (mounted) setKronosAvailable(false);
      });
    return () => { mounted = false; };
  }, [user.username]);

  if (user.username !== 'khanh') return <Navigate to="/dashboard" replace />;

  const mutate = async (action, successMessage) => {
    setBusy(true);
    try { await action(); enqueueSnackbar(successMessage, { variant: 'success' }); await load(); }
    catch (error) { enqueueSnackbar(error.response?.data?.error || 'Thao tác thất bại', { variant: 'error' }); }
    finally { setBusy(false); }
  };

  const confirmOrder = async () => {
    setBusy(true);
    try {
      await tradeOrderService.place(order.symbol, order.side);
      enqueueSnackbar(`Đã vào lệnh ${order.side} ${order.symbol}`, { variant: 'success' });
      setOrder(null);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Vào lệnh thất bại', { variant: 'error' });
    } finally { setBusy(false); }
  };

  const checkKronos = async (item) => {
    const symbol = String(item.symbol || '').toUpperCase();
    const watchSide = String(item.watch_side || item.watchSide || '').toUpperCase();
    setKronosBusySymbol(symbol);
    try {
      const response = await api.get(KRONOS.FORECAST, {
        params: {
          symbol,
          timeframe: '15m',
          lookback: 400,
          predLen: 24,
          rawLimit: 1000,
        },
      });
      const data = response.data?.data || {};
      const forecastRows = data.forecast || [];
      const first = forecastRows[0];
      const last = forecastRows[forecastRows.length - 1];
      const currentClose = Number(data.latestClose || item.price);
      const lastClose = Number(last?.close);
      const deltaPct = Number.isFinite(currentClose) && currentClose !== 0 && Number.isFinite(lastClose)
        ? ((lastClose - currentClose) / currentClose) * 100
        : null;
      const forecastSide = deltaPct === null
        ? 'UNKNOWN'
        : Math.abs(deltaPct) < KRONOS_MIN_EDGE_PCT
          ? 'FLAT'
          : deltaPct > 0
            ? 'LONG'
            : 'SHORT';

      setKronosResult({
        symbol,
        watchSide,
        forecastSide,
        deltaPct,
        currentClose,
        first,
        last,
        matches: forecastSide === watchSide,
      });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || `Kronos không check được ${symbol}`, { variant: 'error' });
    } finally {
      setKronosBusySymbol('');
    }
  };

  return (
    <Box>
      <AppBar position="static"><Toolbar>
        <IconButton edge="start" color="inherit" onClick={() => navigate('/dashboard')}><ArrowBackIcon /></IconButton>
        <Typography variant="h6" sx={{ flexGrow: 1, ml: 1 }}>Quản lý Symbol</Typography>
        <IconButton color="inherit" onClick={load} disabled={loading}><RefreshIcon /></IconButton>
      </Toolbar></AppBar>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper sx={{ p: 2, mb: 2 }}><SymbolSelectWithWatchButton watchedSymbols={watchedSymbols} busy={busy} onWatch={(symbol, side) => mutate(() => watchlistService.add(symbol, side), `Đang theo dõi ${symbol} ${side}`)} onUnwatch={(symbol) => mutate(() => watchlistService.remove(symbol), `Đã bỏ theo dõi ${symbol}`)} /></Paper>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" color="text.secondary">ĐANG THEO DÕI</Typography>
            <Chip size="small" label={`${items.length} symbols`} />
            <Chip size="small" color="success" variant="outlined" label={`${counts.LONG} LONG`} />
            <Chip size="small" color="error" variant="outlined" label={`${counts.SHORT} SHORT`} />
          </Stack>
        </Stack>
        {kronosResult ? (
          <Alert
            severity={['FLAT', 'UNKNOWN'].includes(kronosResult.forecastSide) ? 'info' : kronosResult.matches ? 'success' : 'warning'}
            sx={{ mb: 2 }}
            onClose={() => setKronosResult(null)}
          >
            <Typography variant="subtitle2">
              Kronos {kronosResult.symbol}: watch {kronosResult.watchSide} / forecast {kronosResult.forecastSide}
              {kronosResult.deltaPct !== null ? ` (${kronosResult.deltaPct.toFixed(2)}%)` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Current: {Number(kronosResult.currentClose || 0).toFixed(6)} | First forecast: {Number(kronosResult.first?.close || 0).toFixed(6)} | Last forecast: {Number(kronosResult.last?.close || 0).toFixed(6)} | Edge min: {KRONOS_MIN_EDGE_PCT}%
            </Typography>
          </Alert>
        ) : null}
        {loading ? <Box textAlign="center" py={8}><CircularProgress /></Box> : (
          <Stack spacing={3}>
            {[
              { side: 'LONG', color: 'success.main', items: groupedItems.LONG },
              { side: 'SHORT', color: 'error.main', items: groupedItems.SHORT },
            ].map((group) => (
              <Box key={group.side}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                  <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: group.color }} />
                  <Typography variant="h6" fontWeight={800} color={group.color}>{group.side}</Typography>
                  <Chip size="small" label={group.items.length} />
                </Stack>
                <Grid container spacing={1.5}>
                  {group.items.map((item) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      key={item.symbol}
                      sx={{ '@media (min-width: 1200px)': { flexBasis: '20%', maxWidth: '20%' } }}
                    >
                      <SymbolCard
                        item={item}
                        busy={busy}
                        kronosBusy={kronosBusySymbol === item.symbol}
                        showKronos={kronosAvailable}
                        onKronosCheck={checkKronos}
                        onOrder={(symbol, side) => setOrder({ symbol, side })}
                        onUnwatch={(symbol) => mutate(() => watchlistService.remove(symbol), `Đã bỏ theo dõi ${symbol}`)}
                      />
                    </Grid>
                  ))}
                  {!group.items.length ? (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ py: 3, textAlign: 'center', bgcolor: 'transparent' }}>
                        <Typography color="text.secondary">Chưa có symbol {group.side}.</Typography>
                      </Paper>
                    </Grid>
                  ) : null}
                </Grid>
              </Box>
            ))}
          </Stack>
        )}
      </Container>
      <OrderConfirmDialog open={Boolean(order)} symbol={order?.symbol} side={order?.side} submitting={busy} onClose={() => setOrder(null)} onConfirm={confirmOrder} />
    </Box>
  );
}
